import React from "react";
import { withRouter } from "react-router-dom";
import { Box, Typography, Button, Container } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import ErrorOutlineIcon from "@material-ui/icons/ErrorOutline";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "60vh",
    padding: theme.spacing(3),
  },
  content: {
    textAlign: "center",
  },
  icon: {
    fontSize: 80,
    color: theme.palette.error.main,
    marginBottom: theme.spacing(2),
  },
  title: {
    marginBottom: theme.spacing(2),
    fontWeight: 500,
  },
  message: {
    marginBottom: theme.spacing(3),
    color: theme.palette.text.secondary,
  },
  button: {
    marginTop: theme.spacing(2),
  },
}));

// Lista de erros que devem ser ignorados e recuperados automaticamente
const RECOVERABLE_ERRORS = [
  "Cannot read properties of null",
  "Cannot read properties of undefined",
  "TypeError: null is not an object",
  "TypeError: undefined is not an object",
  "Cannot read property",
  "is not a function",
  "Network Error",
  "Failed to fetch",
  "Load failed",
];

const CHUNK_RELOAD_STORAGE_KEY = "chunk-load-hard-reload-attempted";

const isChunkLoadError = (error) => {
  if (!error) return false;
  const message = String(error.message || "");
  const stack = String(error.stack || "");
  return (
    message.includes("ChunkLoadError") ||
    message.includes("Loading chunk") ||
    stack.includes("ChunkLoadError")
  );
};

// Verifica se o erro é recuperável
const isRecoverableError = (error) => {
  if (!error || !error.message) return true;
  return RECOVERABLE_ERRORS.some(pattern => 
    error.message.toLowerCase().includes(pattern.toLowerCase())
  );
};

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null, 
      errorInfo: null,
      retryCount: 0,
      isRecovering: false 
    };
    this.maxRetries = 3;
    this.retryTimeout = null;
  }

  static getDerivedStateFromError(error) {
    // Verifica se é erro recuperável
    if (isRecoverableError(error)) {
      return { hasError: false, isRecovering: true };
    }
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error("ErrorBoundary caught an error:", error, errorInfo);

    if (isChunkLoadError(error)) {
      const hasReloaded = sessionStorage.getItem(CHUNK_RELOAD_STORAGE_KEY) === "1";

      if (!hasReloaded) {
        sessionStorage.setItem(CHUNK_RELOAD_STORAGE_KEY, "1");
        console.warn("[ErrorBoundary] ChunkLoadError detectado. Executando hard reload para sincronizar assets.");
        window.location.reload();
        return;
      }
    }
    
    // Se for erro recuperável e não excedeu retries, tenta recuperar
    if (isRecoverableError(error) && this.state.retryCount < this.maxRetries) {
      console.log(`[ErrorBoundary] Erro recuperável detectado, tentando recuperar... (tentativa ${this.state.retryCount + 1}/${this.maxRetries})`);
      
      // Limpa timeout anterior se existir
      if (this.retryTimeout) {
        clearTimeout(this.retryTimeout);
      }
      
      // Aguarda um pouco antes de recuperar para evitar loop
      this.retryTimeout = setTimeout(() => {
        this.setState(prevState => ({
          hasError: false,
          isRecovering: false,
          retryCount: prevState.retryCount + 1,
          error: null,
          errorInfo: null
        }));
      }, 100);
      
      return;
    }
    
    // Erro não recuperável ou excedeu retries
    this.setState({
      error,
      errorInfo,
      hasError: true,
      isRecovering: false
    });
  }

  componentWillUnmount() {
    if (this.retryTimeout) {
      clearTimeout(this.retryTimeout);
    }
  }

  handleGoHome = () => {
    this.setState({ hasError: false, error: null, errorInfo: null, retryCount: 0, isRecovering: false });
    this.props.history.push("/");
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    // Se está recuperando, renderiza normalmente
    if (this.state.isRecovering) {
      return this.props.children;
    }

    if (this.state.hasError) {
      return <ErrorFallback onGoHome={this.handleGoHome} onReload={this.handleReload} />;
    }

    if (sessionStorage.getItem(CHUNK_RELOAD_STORAGE_KEY) === "1") {
      sessionStorage.removeItem(CHUNK_RELOAD_STORAGE_KEY);
    }

    return this.props.children;
  }
}

const ErrorFallback = ({ onGoHome, onReload }) => {
  const classes = useStyles();

  return (
    <Container className={classes.root}>
      <Box className={classes.content}>
        <ErrorOutlineIcon className={classes.icon} />
        <Typography variant="h4" className={classes.title}>
          {i18n.t("errorBoundary.title")}
        </Typography>
        <Typography variant="body1" className={classes.message}>
          {i18n.t("errorBoundary.message")}
        </Typography>
        <Box>
          <Button
            variant="contained"
            color="primary"
            onClick={onGoHome}
            className={classes.button}
          >
            {i18n.t("errorBoundary.goHome")}
          </Button>
          <Button
            variant="outlined"
            color="primary"
            onClick={onReload}
            className={classes.button}
            style={{ marginLeft: 16 }}
          >
            {i18n.t("errorBoundary.reload")}
          </Button>
        </Box>
      </Box>
    </Container>
  );
};

export default withRouter(ErrorBoundary);
